import mixpanel from 'mixpanel-browser';
import { createLogger } from '../../common/logger';
import { errorTracker } from '../../common/error-tracker';
import type { AnalyticsEventMap } from './events';

const log = createLogger({ defaultContext: { service: 'analytics' } });

let initialized = false;

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function isProduction(): boolean {
  try {
    return (
      typeof process !== 'undefined' &&
      process.env['NODE_ENV'] === 'production'
    );
  } catch {
    return false;
  }
}

export interface AnalyticsConfig {
  readonly token: string | undefined;
}

export function initAnalytics(config: AnalyticsConfig): void {
  if (!isBrowser()) return;

  if (config.token) {
    mixpanel.init(config.token, { persistence: 'localStorage' });
    initialized = true;
    log.debug('Analytics initialized with Mixpanel');
  } else if (!isProduction()) {
    log.info(
      'Analytics running in no-op mode — events will be logged to console',
    );
  }
}

export function track<E extends keyof AnalyticsEventMap>(
  event: E,
  properties: AnalyticsEventMap[E],
): void {
  if (!isBrowser()) return;

  try {
    if (initialized) {
      mixpanel.track(event, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    } else if (!isProduction()) {
      log.debug('Analytics event (no-op)', { event, properties });
    }
  } catch (err: unknown) {
    errorTracker.logMessage('Analytics track failed', { event, err }, 'warn');
  }
}

export function identify(userId: string): void {
  if (!isBrowser()) return;

  try {
    if (initialized) {
      mixpanel.identify(userId);
    } else if (!isProduction()) {
      log.debug('Analytics identify (no-op)', { userId });
    }
  } catch (err: unknown) {
    errorTracker.logMessage('Analytics identify failed', { userId, err }, 'warn');
  }
}

export function reset(): void {
  if (!isBrowser()) return;

  try {
    if (initialized) {
      mixpanel.reset();
    } else if (!isProduction()) {
      log.debug('Analytics reset (no-op)');
    }
  } catch (err: unknown) {
    errorTracker.logMessage('Analytics reset failed', { err }, 'warn');
  }
}

export function resetAnalyticsState(): void {
  initialized = false;
}
